import { useCallback, useContext, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { inputStyles } from '@/styles/input';
import { CheckoutContext } from '@/context/CheckoutContext';
import { CardBrandIcons } from '../PaymentInputs/CardBrandIcons';
import { NUVEI_FIELD_IDS } from '@/constants/nuvei';
import { Loader } from '../Common/Loader';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useNuvei } from '@/hooks/useNuvei';
import { Input } from '../Common/Input';
import { useFormValidation } from '@/hooks/useFormValidation';
import { ValidationRules } from '@/utils/validation';
import { Email } from '../Common/Email';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { useTranslate } from '@/hooks/useTranslate';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import { isEmailValid } from '@/utils/email';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';

export const NuveiCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    featureFlags,
    template,
    actions: { setIsFormValid },
    card: { initialCardPayload, setInitialCardPayload },
  } = context;
  const { isLoading, error, isFieldsValid, fieldsError } = useNuvei();
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const { t } = useTranslate();
  const {
    values,
    errors,
    touched,
    setValue,
    setFieldTouched,
    isValid: cardholderNameValid,
  } = useFormValidation(
    { cardholderName: initialCardPayload?.cardholderName ?? '' },
    {
      cardholderName: [
        ValidationRules.required(t('cardholderName'), t('fieldErrors.incomplete', { field: t('cardholderName') })),
        ValidationRules.cardholderName(t('fieldErrors.incompleteCardholderName')),
      ],
    }
  );

  useEffect(() => {
    const isFormValid =
      cardholderNameValid && isFieldsValid && !isLoading && isZipCodeValid && isEmailValid(customer?.email);
    setIsFormValid(isFormValid);
  }, [
    cardholderNameValid,
    isFieldsValid,
    isLoading,
    isZipCodeValid,
    customer?.email,
    setIsFormValid,
    values.cardholderName,
  ]);

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue('cardholderName', value);
    setInitialCardPayload({ ...initialCardPayload, cardholderName: value });
  };

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (error) {
    logError(ErrorLogs.nuveiCardFormError, getLogInfoData(context));
    return <PaymentMethodNotSupported />;
  }

  return (
    <div className="flex flex-col gap-2">
      <Input
        type="text"
        placeholder={t('cardholderName')}
        value={values.cardholderName}
        onChange={handleCardholderNameChange}
        onBlur={() => setFieldTouched('cardholderName')}
        error={touched.cardholderName ? errors.cardholderName : undefined}
        className="w-full h-11 text-base"
      />
      <div className="flex flex-col gap-1">
        <div className="relative">
          <div id={NUVEI_FIELD_IDS.CARD_NUMBER} className={cn(inputStyles, 'w-full h-11')} />
          <CardBrandIcons />
        </div>
        {fieldsError.cardNumber && <div className="text-xs text-red-500">{fieldsError.cardNumber}</div>}
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <div id={NUVEI_FIELD_IDS.EXPIRATION} className={cn(inputStyles, 'w-full h-11')} />
          {fieldsError.expiration && <div className="text-xs text-red-500">{fieldsError.expiration}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div id={NUVEI_FIELD_IDS.CVC} className={cn(inputStyles, 'w-full h-11')} />
          {fieldsError.cvc && <div className="text-xs text-red-500">{fieldsError.cvc}</div>}
        </div>
      </div>
      <InputTaxComponent onValidate={handleTaxValidationChange} />
      {!shouldHideEmail(featureFlags, customer, template.features) && (
        <div className="w-full">
          <Email />
        </div>
      )}
      {isLoading && <Loader />}
    </div>
  );
};
